import React, { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDismissablePopover } from '@kiao-voice/hooks'
import { CalendarDays } from 'lucide-react'
import { DayPicker, type DateRange } from 'react-day-picker'
import { createPortal } from 'react-dom'
import 'react-day-picker/style.css'
import { Button } from './button'
import { cn } from './cn'

export interface DateRangeValue {
  from?: string
  to?: string
}

export interface DateRangeFieldLabels {
  placeholder: string
  dialogLabel: string
  clear: string
  done: string
}

export interface DateRangeFieldProps {
  label: string
  value: DateRangeValue | null
  onChange: (value: DateRangeValue | null) => void
  min?: string
  max?: string
  disabled?: boolean
  labels?: Partial<DateRangeFieldLabels>
  className?: string
}

interface PopoverPosition {
  top: number
  left: number
  maxHeight: number
  maxWidth: number
}

const DEFAULT_LABELS: DateRangeFieldLabels = {
  placeholder: 'Pick date range',
  dialogLabel: 'Pick a date range',
  clear: 'Clear',
  done: 'Done',
}

function toDate(value?: string): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? undefined : date
}

function toDateString(value: Date): string {
  const pad = (number: number) => String(number).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
}

function formatDate(value?: string): string {
  return value ? value.replaceAll('-', '/') : ''
}

/**
 * A single date-range control backed by one calendar. The first selected day
 * starts a range; the second completes it. Values intentionally use date-only
 * ISO strings to avoid timezone conversion in reporting and filter surfaces.
 */
export function DateRangeField({
  label,
  value,
  onChange,
  min,
  max,
  disabled,
  labels,
  className,
}: DateRangeFieldProps) {
  const text = { ...DEFAULT_LABELS, ...labels }
  const fallbackId = useId()
  const panelId = `${fallbackId}-popover`
  const panelRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { open, toggle, close, containerRef, triggerRef } = useDismissablePopover<
    HTMLDivElement,
    HTMLButtonElement
  >({ additionalContainerRef: panelRef, preventScrollOnFocusRestore: true })
  const portalHost = open ? (triggerRef.current?.closest('dialog') ?? document.body) : null
  const [position, setPosition] = useState<PopoverPosition | null>(null)

  const selected = useMemo<DateRange | undefined>(() => {
    const from = toDate(value?.from)
    const to = toDate(value?.to)
    return from ? { from, to } : undefined
  }, [value?.from, value?.to])
  const minDate = toDate(min)
  const maxDate = toDate(max)
  const disabledDays = useMemo(() => {
    const rules: Array<{ before: Date } | { after: Date }> = []
    if (minDate) rules.push({ before: minDate })
    if (maxDate) rules.push({ after: maxDate })
    return rules
  }, [minDate, maxDate])
  const display = value?.from
    ? value.to
      ? `${formatDate(value.from)} – ${formatDate(value.to)}`
      : `${formatDate(value.from)} –`
    : text.placeholder

  useLayoutEffect(() => {
    if (!open || !portalHost) {
      setPosition(null)
      return
    }
    const reposition = () => {
      const trigger = triggerRef.current
      const panel = panelRef.current
      const content = contentRef.current
      if (!trigger || !panel || !content) return
      const rect = trigger.getBoundingClientRect()
      const isBodyPortal = portalHost === document.body
      const hostRect = isBodyPortal
        ? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
        : portalHost.getBoundingClientRect()
      const hostTop = hostRect.top + (isBodyPortal ? 0 : portalHost.clientTop)
      const hostLeft = hostRect.left + (isBodyPortal ? 0 : portalHost.clientLeft)
      const hostHeight = isBodyPortal ? hostRect.height : portalHost.clientHeight || hostRect.height
      const hostWidth = isBodyPortal ? hostRect.width : portalHost.clientWidth || hostRect.width
      const gap = 6
      const edgeGap = 8
      const topEdge = Math.max(hostTop, 0) + edgeGap
      const bottomEdge = Math.min(hostTop + hostHeight, window.innerHeight) - edgeGap
      const leftEdge = Math.max(hostLeft, 0) + edgeGap
      const rightEdge = Math.min(hostLeft + hostWidth, window.innerWidth) - edgeGap
      const panelHeight = Math.max(panel.offsetHeight, content.scrollHeight)
      const panelWidth = Math.max(panel.offsetWidth, content.scrollWidth)
      const belowTop = rect.bottom + gap
      const aboveTop = rect.top - gap - panelHeight
      const top =
        belowTop + panelHeight <= bottomEdge || aboveTop < topEdge
          ? Math.max(topEdge, Math.min(belowTop, bottomEdge - panelHeight))
          : aboveTop
      const left = Math.max(leftEdge, Math.min(rect.left, rightEdge - panelWidth))
      const maxHeight = Math.max(0, bottomEdge - top)
      const maxWidth = Math.max(0, rightEdge - left)
      setPosition({
        top: isBodyPortal ? top : top - hostTop + portalHost.scrollTop,
        left: isBodyPortal ? left : left - hostLeft + portalHost.scrollLeft,
        maxHeight,
        maxWidth,
      })
    }
    reposition()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reposition)
    if (observer) {
      if (contentRef.current) observer.observe(contentRef.current)
      if (triggerRef.current) observer.observe(triggerRef.current)
    }
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      observer?.disconnect()
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, portalHost, triggerRef])

  const handleSelect = useCallback(
    (next: DateRange | undefined) => {
      if (!next?.from) {
        onChange(null)
        return
      }
      onChange({ from: toDateString(next.from), to: next.to ? toDateString(next.to) : undefined })
    },
    [onChange]
  )

  return (
    <div className={cn('relative w-[var(--fw-md)] max-w-full', className)} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          'flex h-[var(--ctl-h)] w-full items-center justify-between gap-2 rounded-[var(--radius)] border border-border bg-background px-3 text-left font-mono text-[length:var(--text-base)] text-foreground focus:border-foreground focus:outline-2 focus:outline-[var(--fg-soft)]',
          !value?.from && 'text-muted'
        )}
        disabled={disabled}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggle}
      >
        <span className="truncate">{display}</span>
        <CalendarDays aria-hidden="true" className="size-4 shrink-0 text-muted" />
      </button>

      {portalHost
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              aria-label={text.dialogLabel}
              className={cn(
                portalHost === document.body ? 'fixed' : 'absolute',
                'z-[80] overflow-auto rounded-[10px] border border-border bg-background p-3 shadow-[var(--shadow)]'
              )}
              style={
                position
                  ? {
                      top: position.top,
                      left: position.left,
                      maxHeight: position.maxHeight,
                      maxWidth: position.maxWidth,
                    }
                  : { visibility: 'hidden' }
              }
            >
              <div ref={contentRef}>
                <DayPicker
                  autoFocus
                  mode="range"
                  selected={selected}
                  onSelect={handleSelect}
                  defaultMonth={selected?.from ?? minDate ?? maxDate}
                  disabled={disabledDays}
                  weekStartsOn={1}
                  classNames={{
                    root: 'rdp-root text-[length:var(--text-sm)]',
                    months: 'relative',
                    month_caption: 'flex h-8 items-center px-1 font-medium',
                    nav: 'absolute right-0 top-0 flex h-8 items-center gap-1',
                    button_previous:
                      'grid h-7 w-7 place-items-center rounded-[6px] text-muted hover:bg-surface hover:text-foreground',
                    button_next:
                      'grid h-7 w-7 place-items-center rounded-[6px] text-muted hover:bg-surface hover:text-foreground',
                    chevron: 'size-4 fill-current',
                    weekdays: 'flex',
                    weekday:
                      'w-8 py-1 text-center font-mono text-3xs font-normal uppercase tracking-[0.08em] text-muted',
                    week: 'flex',
                    day: 'p-0',
                    day_button:
                      'grid size-8 place-items-center rounded-[6px] font-mono text-[length:var(--text-xs)] hover:bg-surface disabled:pointer-events-none disabled:opacity-35',
                    range_start: '[&>button]:bg-foreground [&>button]:text-background',
                    range_end: '[&>button]:bg-foreground [&>button]:text-background',
                    range_middle: '[&>button]:rounded-none [&>button]:bg-surface',
                    today: 'font-semibold',
                    outside: 'opacity-35',
                    hidden: 'invisible',
                  }}
                />
                <div className="mt-2 flex items-center justify-end gap-1 border-t border-border pt-2.5">
                  <Button variant="ghost" size="sm" className="px-2 py-1 text-[length:var(--text-xs)]" onClick={() => { onChange(null); close(true) }}>
                    {text.clear}
                  </Button>
                  <Button size="sm" onClick={() => close(true)}>
                    {text.done}
                  </Button>
                </div>
              </div>
            </div>,
            portalHost
          )
        : null}
    </div>
  )
}
